// =============================================================================
// CHART GRANULARITY — adaptive daily/weekly/monthly rollup for time-series charts
// =============================================================================
// A daily line over many months is visual noise (165 jagged points). This rolls
// daily points up to weekly or monthly buckets based on the selected span, so wide
// ranges stay readable. Used by the Cash & Revenue chart (useOperationsAnalytics).
//
// The output shape matches what FinancialGraph consumes ({ date, cash, revenue }).
// FinancialGraph derives the on-point value labels itself, so we don't set them here.
// The `date` strings are crafted to render correctly through FinancialGraph.formatDateLabel,
// which splits on "/" ("6/9" -> "Jun 9") and passes non-slash strings through ("Jun").
// =============================================================================

import { formatDateShort } from "./formatters";

export type ChartGranularity = "daily" | "weekly" | "monthly";

export interface DailyPoint {
  ts: number; // epoch ms, anchored at local noon of the day
  cash: number;
  revenue: number;
}

export interface ChartPoint {
  date: string;
  cash: number;
  revenue: number;
  label: string; // recomputed downstream by FinancialGraph; kept for shape stability
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Thresholds (Mako-confirmed Jun 15 2026): <=31d daily, 32-92d weekly, >92d monthly.
const GRANULARITY_DAILY_MAX = 31;
const GRANULARITY_WEEKLY_MAX = 92;

/** Pick resolution from the selected range span (in days). */
export function pickChartGranularity(spanDays: number): ChartGranularity {
  if (spanDays <= GRANULARITY_DAILY_MAX) return "daily";
  if (spanDays <= GRANULARITY_WEEKLY_MAX) return "weekly";
  return "monthly";
}

/** Monday-anchored start of the week for a given local date. */
function startOfWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
  const dow = x.getDay(); // 0=Sun..6=Sat
  const shift = dow === 0 ? -6 : 1 - dow; // back to Monday
  x.setDate(x.getDate() + shift);
  return x;
}

/**
 * Aggregate ascending daily points into the chosen granularity. Daily passes through
 * (one bucket per day). Weekly sums Mon-Sun. Monthly sums per calendar month. Cash &
 * revenue are summed per bucket; the bucket's `date` is its display label.
 */
export function aggregateDailySeries(
  points: DailyPoint[],
  granularity: ChartGranularity
): ChartPoint[] {
  if (points.length === 0) return [];

  if (granularity === "daily") {
    return points.map((p) => ({
      date: formatDateShort(new Date(p.ts)),
      cash: p.cash,
      revenue: p.revenue,
      label: "",
    }));
  }

  // Whether the span crosses calendar years — if so, monthly labels need a year suffix
  // ("Jun '25" vs "Jun '26") to stay unambiguous.
  const firstYear = new Date(points[0].ts).getFullYear();
  const lastYear = new Date(points[points.length - 1].ts).getFullYear();
  const multiYear = firstYear !== lastYear;

  // Bucket in order; a Map preserves insertion order so the result stays ascending.
  const buckets = new Map<string, { date: string; cash: number; revenue: number }>();

  for (const p of points) {
    const d = new Date(p.ts);
    let key: string;
    let label: string;

    if (granularity === "weekly") {
      const wk = startOfWeekMonday(d);
      key = String(wk.getTime());
      label = formatDateShort(wk); // "M/D/YYYY" -> renders "Mon D" (week start)
    } else {
      // monthly
      key = `${d.getFullYear()}-${d.getMonth()}`;
      label = multiYear
        ? `${MONTH_ABBR[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`
        : MONTH_ABBR[d.getMonth()];
    }

    const existing = buckets.get(key);
    if (existing) {
      existing.cash += p.cash;
      existing.revenue += p.revenue;
    } else {
      buckets.set(key, { date: label, cash: p.cash, revenue: p.revenue });
    }
  }

  return Array.from(buckets.values()).map((b) => ({ ...b, label: "" }));
}
